<script lang="ts" setup>
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { uuid } from '@/composables'
import { useNotifyStore } from '@/stores'
import {
  NotificationPrefs as NotificationPrefsSchema,
  type NotificationPrefs,
  type NotificationPrefsUpdateRequest
} from '@/types'
import { STATUS_CODES } from '@/constants'

const { t } = useI18n()
const notifyStore = useNotifyStore()

const prefs = reactive<NotificationPrefs>({
  vserver_down: false,
  vserver_disk_usage: false
})
const loading = ref(true)
const saving = ref(false)

async function fetchPrefs() {
  loading.value = true
  try {
    const res = await useAxios<null, NotificationPrefs>('GET', '/account/notification-prefs', true)
    if (res?.status === STATUS_CODES.OK) {
      const parsed = NotificationPrefsSchema.safeParse(res.data)
      if (parsed.success) {
        Object.assign(prefs, parsed.data)
      }
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AccountPageNotifications:fetch' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('errors.loadFailed'),
      message: getErrorMessage(err)
    })
  } finally {
    loading.value = false
  }
}

async function updatePref(field: keyof NotificationPrefs, value: boolean) {
  const previous = prefs[field]
  prefs[field] = value
  saving.value = true
  try {
    const body: NotificationPrefsUpdateRequest = { [field]: value }
    const res = await useAxios<NotificationPrefsUpdateRequest, NotificationPrefs>(
      'PUT',
      '/account/notification-prefs',
      true,
      body
    )
    if (res?.status === STATUS_CODES.OK) {
      const parsed = NotificationPrefsSchema.safeParse(res.data)
      if (parsed.success) {
        Object.assign(prefs, parsed.data)
      }
    }
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.account.notifications.title'),
      message: t('dashboard.account.notifications.saved')
    })
  } catch (err) {
    prefs[field] = previous
    handleAxiosError(err, { logPrefix: 'AccountPageNotifications:update' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(err)
    })
  } finally {
    saving.value = false
  }
}

onMounted(fetchPrefs)
</script>

<template>
  <div class="notifications-page">
    <div class="page-header">
      <div class="header-left">
        <div class="header-icon">
          <v-icon icon="mdi-bell-outline" size="20" />
        </div>
        <div class="header-text">
          <h2 class="page-title">{{ $t('dashboard.account.notifications.title') }}</h2>
          <p class="page-subtitle">{{ $t('dashboard.account.notifications.subtitle') }}</p>
        </div>
      </div>
    </div>

    <div class="page-content">
      <div v-if="loading" class="loading-state dashboard-loading-state">
        <v-progress-circular indeterminate size="32" width="2" color="primary" />
        <span>{{ $t('common.loading') }}</span>
      </div>

      <div v-else class="pref-list">
        <div class="pref-row">
          <div class="pref-text">
            <span class="pref-label">{{ $t('dashboard.account.notifications.vserverDown.label') }}</span>
            <span class="pref-hint">{{ $t('dashboard.account.notifications.vserverDown.hint') }}</span>
          </div>
          <v-switch
            :model-value="prefs.vserver_down"
            color="primary"
            density="compact"
            hide-details
            :disabled="saving"
            @update:model-value="(v) => updatePref('vserver_down', !!v)"
          />
        </div>

        <div class="pref-row">
          <div class="pref-text">
            <span class="pref-label">{{ $t('dashboard.account.notifications.vserverDiskUsage.label') }}</span>
            <span class="pref-hint">{{ $t('dashboard.account.notifications.vserverDiskUsage.hint') }}</span>
          </div>
          <v-switch
            :model-value="prefs.vserver_disk_usage"
            color="primary"
            density="compact"
            hide-details
            :disabled="saving"
            @update:model-value="(v) => updatePref('vserver_disk_usage', !!v)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notifications-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.08));
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 10px;
  color: #64b5f6;
}

.page-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.page-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.page-content {
  min-height: 120px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  color: rgba(200, 218, 255, 0.6);
}

.pref-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pref-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
}

.pref-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pref-label {
  font-size: 0.92rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.pref-hint {
  font-size: 0.82rem;
  color: rgba(200, 218, 255, 0.55);
}

/* Light Theme */
.v-theme--lightTheme {
  .page-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .page-subtitle,
  .pref-hint {
    color: rgba(15, 23, 42, 0.6);
  }

  .pref-label {
    color: rgba(15, 23, 42, 0.9);
  }
}
</style>
